import { useState, useMemo } from 'react'

import { useLocales } from 'src/contexts'
import { LOCALE_DATES } from 'src/constants'

import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { useManageFuelHistory } from 'src/hooks/useManageFuelHistory'
import { useGetColorThemeAndNext } from 'src/hooks/useGetColorThemeAndNext'
import { useConvertCurrency } from 'src/hooks/useConvertCurrency'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { useAppDispatch } from 'src/hooks/useAppDispatch'
import { timestampToDate } from 'src/helpers/timestampToDate'

import { PeriodChartInMonth } from 'src/components/HomeCharBar/types'
import {
  getChartConfig,
  getMappedData,
  mapCostHistory,
} from 'src/components/HomeCharBar/helpers'

import { userSlice } from 'src/store/reducers/UserReducer/UserSlice'

export const useHomeCharBar = () => {
  const { translations, locale } = useLocales()
  const { mainThemeColor, nextThemeColor } = useGetColorThemeAndNext()
  const { currency, chartPeriodHome } = useAppSelector(state => state.user)
  const { setChartPeriodHome } = userSlice.actions
  const dispatch = useAppDispatch()

  const { allFuelHistory } = useManageFuelHistory()
  const { allMaintenanceHistory } = useManageMaintenanceHistory()

  const { convertCurrency } = useConvertCurrency()

  const [period, setPeriod] = useState(
    chartPeriodHome || PeriodChartInMonth.THREE_MONTH,
  )

  const chartConfig = getChartConfig(
    translations.homePage.barChart,
    mainThemeColor,
    nextThemeColor,
  )

  const handleSetPeriod = (value: string): void => {
    setPeriod(value as PeriodChartInMonth)
    dispatch(setChartPeriodHome(value as PeriodChartInMonth))
  }

  const periodStartDate = useMemo(() => {
    const now = new Date()
    now.setMonth(now.getMonth() - parseInt(period))

    return now
  }, [period])

  const filteredFuelHistory = useMemo(() => {
    return (
      allFuelHistory?.filter(item => {
        const date = timestampToDate(item.dateOfRefuel)

        return date >= periodStartDate
      }) || []
    )
  }, [allFuelHistory, periodStartDate])

  const filteredMaintenanceHistory = useMemo(() => {
    return (
      allMaintenanceHistory?.filter(item => {
        const date = timestampToDate(item.date)

        return date >= periodStartDate
      }) || []
    )
  }, [allMaintenanceHistory, periodStartDate])

  const chartData = useMemo(() => {
    const mapFuelHistory = mapCostHistory(
      'totalCost',
      'dateOfRefuel',
      'currency',
      filteredFuelHistory,
    )
    const mapMaintenanceHistory = mapCostHistory(
      'serviceCost',
      'date',
      'currency',
      filteredMaintenanceHistory,
    )

    return getMappedData(
      mapFuelHistory,
      mapMaintenanceHistory,
      currency,
      convertCurrency,
    )
  }, [allFuelHistory, allMaintenanceHistory, period])

  const [activeChart, setActiveChart] =
    useState<keyof typeof chartConfig>('fuel')

  const total = useMemo(
    () => ({
      maintenance: chartData.reduce((acc, curr) => acc + curr.maintenance, 0),
      fuel: chartData.reduce((acc, curr) => acc + curr.fuel, 0),
    }),
    [chartData],
  )

  return {
    activeChart,
    setActiveChart,
    chartConfig,
    total,
    chartData,
    locale: translations.homePage.barChart,
    currency,
    localCode: LOCALE_DATES[locale],
    views: Object.keys(chartConfig).filter(key => key !== 'views'),
    period,
    handleSetPeriod,
    periods: Object.values(PeriodChartInMonth),
  }
}
